import React, { createContext, useContext, useState, ReactNode } from 'react';
import { DateRange } from '@/types/date';
import { subDays, subMonths, subYears } from 'date-fns';

interface DateRangeContextType {
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  selectedPreset: string;
  setSelectedPreset: (preset: string) => void;
}

const DateRangeContext = createContext<DateRangeContextType | undefined>(undefined);

export const useDateRange = () => {
  const context = useContext(DateRangeContext);
  if (!context) {
    throw new Error('useDateRange must be used within a DateRangeProvider');
  }
  return context;
};

interface DateRangeProviderProps {
  children: ReactNode;
}

const getPresetDateRange = (preset: string): DateRange => {
  const today = new Date();

  switch (preset) {
    case 'Last 7 days':
      return { from: subDays(today, 7), to: today };
    case 'Last 30 days':
      return { from: subDays(today, 30), to: today };
    case 'Last 3 months':
      return { from: subMonths(today, 3), to: today };
    case 'Last year':
    default:
      return { from: subYears(today, 1), to: today };
  }
};

export const DateRangeProvider: React.FC<DateRangeProviderProps> = ({ children }) => {
  // Default date range: 1 August 2025 to 31 August 2025
  const defaultDateRange: DateRange = {
    from: new Date(2025, 7, 1), // Month is 0-indexed, so 7 = August
    to: new Date(2025, 7, 31),
  };

  const [selectedPreset, setSelectedPreset] = useState('Custom');
  const [dateRange, setDateRange] = useState<DateRange>(defaultDateRange);

  const handleSetSelectedPreset = (preset: string) => {
    setSelectedPreset(preset);
    setDateRange(getPresetDateRange(preset));
  };

  return (
    <DateRangeContext.Provider
      value={{
        dateRange,
        setDateRange,
        selectedPreset,
        setSelectedPreset: handleSetSelectedPreset,
      }}
    >
      {children}
    </DateRangeContext.Provider>
  );
};
